/**
 * The record the server put in the page.
 *
 * An article page used to reach its content down a chain: the browser fetched
 * the HTML, parsed the JavaScript, waited for the whole `/site/bootstrap` feed so
 * the pool would know the article existed, and only then asked for that article's
 * body. Four steps, each waiting on the one before, and the body is the largest
 * thing on the page, so the largest paint waited for all of them.
 *
 * `api/html.js` already fetches the record to build the share tags, so it now
 * writes it into the page as well. Reading it here collapses the chain to one
 * step: the page has its article before a single request of its own is made.
 *
 * The pool still wins once it arrives, so an editor's change during a visit shows
 * up as it did before. This only fills the gap before that.
 */
import type { Doc } from './siteContent';

const ELEMENT_ID = '__gavel_record__';

interface Inlined {
    collection: string;
    slug: string;
    record: Doc;
}

/** Parsed once: the element never changes after the document is written. */
let parsed: Inlined | null | undefined;

function read(): Inlined | null {
    if (parsed !== undefined) return parsed;
    parsed = null;
    if (typeof document === 'undefined') return parsed;
    const text = document.getElementById(ELEMENT_ID)?.textContent;
    if (text) {
        try {
            const value = JSON.parse(text) as Partial<Inlined>;
            if (value && typeof value.collection === 'string' && typeof value.slug === 'string' && value.record) {
                parsed = { collection: value.collection, slug: value.slug, record: value.record as Doc };
            }
        } catch {
            /* Malformed for any reason: fall back to fetching, as before. */
        }
    }
    return parsed;
}

/**
 * The inlined record for this collection and slug, or null when the page carries
 * none — a different article, a listing page, or a dev server with no function in
 * front of it.
 */
export function inlinedRecord(collection: 'articles' | 'stories', slug?: string): Doc | null {
    if (!slug) return null;
    const found = read();
    return found && found.collection === collection && found.slug === slug ? found.record : null;
}
